import { Formik } from "formik";
import { PrimarySubmitButton } from "src/reusable/Buttons";
import Card from "src/reusable/Card";
import { Input } from "src/reusable/Form";
import * as Yup from "yup";

import { Switch } from "@mobiscroll/react";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { useQuery } from "react-query";
import FormikTextarea from "src/reusable/Form/input/Textarea";
import FormikSelect from "src/reusable/Form/select/Select";
import { createRazorpayPlan, deactivateAllRazorpayPlan, getDocgTaxSettings, listActiveRazorpayPlans, listAllRazorpayPlans, } from "./api";
import TaxSettingsCard from "./TaxSettings";
import Loader from "src/components/Dashboard/common/Loader";
import { buildPlanGstVerbiage, splitInclusiveGstRupees } from "src/utils/gstBreakup";


const SubscriptionPlanSchema = Yup.object().shape({
  // minPatientsAllowed: Yup.number().integer().required("*Required"),
  // maxPatientsAllowed: Yup.number().integer().required("*Required"),
  // unit: Yup.string().required("*Required"),
  amount: Yup.number().required("*Required"),
  companyPercentage: Yup.number().required("*Required"),
  interval: Yup.number().integer().min(1, "Must be at least 1").required("*Required"),
  period: Yup.string().required("*Required"),
  description: Yup.string().required("*Required"),
  name: Yup.string().required("*Required"),
  subscriptionType: Yup.string().required("*Required"),
});

const SubscriptionPlanForm = ({ refetch, setLoader }) => {
  const { data: taxSettings } = useQuery(["docgTaxSettings"], getDocgTaxSettings);
  const sacCode = taxSettings?.sacCode ?? "998314";
  const gstRatePercent = Number(taxSettings?.gstRatePercent ?? 18);

  const onSubmit = async (
    {
      name,
      subscriptionType,
      amount,
      interval,
      period,
      minPatientsAllowed,
      maxPatientsAllowed,
      unit,
      description,
      companyPercentage,
      applicableTaxVerbiage,
      verbiageForTransactFee,
      onboardingFee,
      reactivationFee,
      bulkDataExportFee
    },
    { resetForm }
  ) => {
    try {
      setLoader(true);
      const taxVerbiage = applicableTaxVerbiage?.trim()
        ? applicableTaxVerbiage
        : buildPlanGstVerbiage(sacCode, gstRatePercent);
      const res = await createRazorpayPlan({
        subscriptionType,
        interval,
        period,
        minPatientsAllowed,
        maxPatientsAllowed,
        unit,
        companyPercentage,
        applicableTaxVerbiage: taxVerbiage,
        verbiageForTransactFee,
        item: {
          name,
          amount,
          description,
          currency: "INR",
        },
        onboardingFee,
        reactivationFee,
        bulkDataExportFee,
      });
      if (res) {
        resetForm();
      }
      refetch();
      refetchPlans();
      return res;
    } catch (error) {
      console.error("Error creating subscription plan:", error);
    } finally {
      setLoader(false);
    }
  };

  const { data, refetch: refetchPlans } = useQuery(
    ["list active subscription plans"],
    async function () {
      const res = await listActiveRazorpayPlans();
      return res;
    }
  );

  return (
    <Card title="Subscription Form">
      <Formik
        validationSchema={SubscriptionPlanSchema}
        initialValues={{
          period: "monthly",
          interval: 1,
          name: "",
          amount: 0,
          description: "",
          subscriptionType: null,
          minPatientsAllowed: 0,
          maxPatientsAllowed: 0,
          companyPercentage: 0,
          unit: "",
          applicableTaxVerbiage: "",
          verbiageForTransactFee: "",
          onboardingFee: 0,
          reactivationFee: 0,
          bulkDataExportFee: 0
        }}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
          const planGst =
            Number(values.amount) > 0
              ? splitInclusiveGstRupees(values.amount, gstRatePercent)
              : null;
          return (
          <>
            <form className="pt-2 pb-3" onSubmit={handleSubmit}>
              <div className="mbsc-grid">
                <div className="mbsc-row">

                  <div className="mbsc-row mbsc-col-12 mt-4">
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input label="Plan name" name="name" type="text" />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        inputStyle="underline"
                        labelStyle="floating"
                        label="Subscription Type"
                        name="subscriptionType"
                        placeholder=""
                        type="text"
                        id="editInput"
                        list="suggestions"
                        cssClass="w-100"
                        autoComplete="off"
                      />
                      <div className="mbsc-col-12 d-flex align-items-end justify-content-center mt-2">
                        <datalist id="suggestions">
                          {data?.map(({ subscriptionType }) => (
                            <option
                              value={subscriptionType}
                              onClick={() => {
                                setFieldValue(
                                  "subscriptionType",
                                  subscriptionType,
                                  true
                                );
                                // setShowSpecializations(false);
                              }}
                            >
                              {subscriptionType}
                            </option>
                          ))}
                        </datalist>
                      </div>
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        step="0.01"
                        label="Plan amount (GST-inclusive, INR)"
                        name="amount"
                        type="number"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input label="Frequency" name="interval" type="number" />
                    </div>
                  </div>

                  {planGst ? (
                    <div className="mbsc-row mbsc-col-12 mt-3">
                      <div className="mbsc-col-12">
                        <div
                          style={{
                            padding: "12px 16px",
                            borderRadius: "8px",
                            border: "1px solid #e5e7eb",
                            background: "#f8faf9",
                          }}
                        >
                          <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
                            GST preview (from platform tax settings)
                          </p>
                          <p style={{ margin: "0 0 4px" }}>
                            <strong>SAC:</strong> {sacCode}
                            {" · "}
                            <strong>GST:</strong> {gstRatePercent}%
                          </p>
                          <p style={{ margin: "0 0 4px" }}>
                            Taxable: ₹{planGst.taxableRupees.toLocaleString("en-IN")}
                            {" · "}
                            GST: ₹{planGst.taxRupees.toLocaleString("en-IN")}
                            {" · "}
                            <strong>Total charged: ₹{Number(values.amount).toLocaleString("en-IN")}</strong>
                          </p>
                          <p style={{ margin: 0, fontSize: "0.85rem", color: "#555" }}>
                            Razorpay plan amount must be GST-inclusive. Clinic GSTIN is not required to subscribe;
                            DocG tax invoices show buyer GSTIN only when the clinic has one on file.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="mbsc-row mbsc-col-12 mt-4">
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <FormikSelect
                        data={[
                          { text: "Daily", value: "daily" },
                          { text: "Weekly", value: "weekly" },
                          { text: "Monthly", value: "monthly" },
                          { text: "Yearly", value: "yearly" },
                        ]}
                        label="Period"
                        name="period"
                        themeVariant="light"
                      />
                    </div>
                    {/* <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Min. Patients allowed"
                        name="minPatientsAllowed"
                        type="number"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Max. Patients allowed"
                        name="maxPatientsAllowed"
                        type="number"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input label="Patients frequency unit" name="unit" placeholder="Per Month" />
                    </div> */}
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Onboarding Fee"
                        name="onboardingFee"
                        type="number"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Bulk Data Export Fee"
                        name="bulkDataExportFee"
                        type="number"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Reactivation Fee"
                        name="reactivationFee"
                        type="number"
                      />
                    </div>
                  </div>

                  <div className="mbsc-row mbsc-col-12 mt-4">
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Company percent.(%)"
                        name="companyPercentage"
                        type="number"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Tax verbiage (optional)"
                        name="applicableTaxVerbiage"
                        placeholder={buildPlanGstVerbiage(sacCode, gstRatePercent)}
                        type="text"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      <Input
                        label="Transaction unit"
                        name="verbiageForTransactFee"
                        placeholder="eg: per transaction"
                        type="text"
                      />
                    </div>
                    <div className="mbsc-col-6 mbsc-col-lg-3">
                      {/* <FormikTextarea label="Description" name="description" /> */}
                      <Input label="Description" name="description" type="text" />
                    </div>
                  </div>



                  {/* <div className="mbsc-row mbsc-col-12 mt-4">
                  </div> */}

                  <div className="mbsc-col-12 mt-4">
                    <i>
                      <small>
                        <b>* Frequency :</b> This is the number of time payment required in a Period. Generally it is “1”.
                      </small>
                    </i>
                    {/* <br />
                    <i>
                      <small>
                        <b>* Patient Unit :</b> The set time period (usually one month) that decides the maximum number of patients allowed.
                      </small>
                    </i> */}
                  </div>

                </div>

                <div className="mbsc-row mt-3">
                  <PrimarySubmitButton isSubmitting={isSubmitting} />
                </div>
              </div>
            </form>
          </>
          );
        }}
      </Formik>
    </Card>
  );
};

const SubscriptionPlan = () => {
  // const [displayRecords, setDisplayRecords] = useState("00");
  // const [recordList, setData] = useState(["00"]);
  const [loader, setLoader] = useState(false);

  const ActivateSwitchClinic = React.useCallback((params) => {
    const checked = params.value;
    const handleChange = async (e) => {

      try {
        setLoader(true);
        await deactivateAllRazorpayPlan(params?.data?.subscriptionType);
        refetch();
      } catch (err) {
        console.error(err);
      } finally {
        setLoader(false);
      }
    };
    return (
      <>
        <div className="w-100 d-flex align-items-center justify-content-center">
          <Switch
            className="docg-table-switch"
            onChange={handleChange}
            checked={checked}
            theme="auto"
            position="start"
            color="success"
            name="noti2"
            id={`patient-${params.rowIndex}`}
          />
        </div>
      </>
    );
  }, []);

  const { data, refetch } = useQuery(
    ["list all subscription plans"],
    async function () {
      const res = await listAllRazorpayPlans();
      res.sort((itm) => itm.activePlan);
      return res;
    }
  );

  const defaultColDef = {
    sortable: true,
    resizable: false,
    filter: true,
    flex: 1,
    minWidth: 150,
    suppressMovable: true,
  };

  return (
    <div className="mbsc-row">
      <Loader show={loader} />
      <div className="mbsc-col-12">
        <SubscriptionPlanForm refetch={refetch} setLoader={setLoader} />
      </div>
      <div className="mbsc-col-12 mt-2">
        <TaxSettingsCard />
      </div>
      <div className="mbsc-col-12 mt-2">
        <Card
          trailing={
            <>
              <button onClick={() => refetch()} className="docg-save-btn">
                Refresh
              </button>
            </>
          }
          title="Subscription List"
        >
          {/* <div className="records-display">
            <span>Showing&nbsp;</span>
            <label className="record-selection-dropdown" htmlFor="">
              <Select
                touchUi={false}
                onChange={onPageSizeChanged}
                value={displayRecords}
                data={recordList}
              />
            </label>
            <span>&nbsp;Records</span>
          </div> */}
          <div
            className="ag-theme-alpine"
            style={{
              height: "100%",
              width: "100%",
            }}
          >
            <AgGridReact
              rowData={data}
              defaultColDef={defaultColDef}
              frameworkComponents={{
                activateSwitchClinic: ActivateSwitchClinic,
              }}
              enableRangeSelection={true}
              pagination={true}
              paginationPageSize={10}
              domLayout={"autoHeight"}
              animateRows={true}
              suppressAggFuncInHeader={true}
              suppressColumnVirtualisation={true}
            >
              <AgGridColumn
                headerName="Plan name"
                field="item.name"
                pinned="left"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Subscription type"
                field="subscriptionType"
                pinned="left"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Description"
                field="item.description"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Min Patients allowed"
                field="minPatientsAllowed"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Max Patients allowed"
                field="maxPatientsAllowed"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Patients unit"
                field="unit"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Amount"
                field="item.amount"
                valueFormatter={(params) =>
                  (params.value / 100).toLocaleString()
                }
              ></AgGridColumn>
              <AgGridColumn
                headerName="Interval"
                field="interval"
              ></AgGridColumn>
              <AgGridColumn headerName="Period" field="period"></AgGridColumn>
              <AgGridColumn
                headerName="Company Percentage"
                field="companyPercentage"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Applicable Tax Verbiage"
                field="applicableTaxVerbiage"
              ></AgGridColumn>
              <AgGridColumn
                headerName="Transaction unit"
                field="verbiageForTransactFee"
              ></AgGridColumn>

              <AgGridColumn
                headerName="Created At"
                field="createdAt"
                valueFormatter={(params) => {
                  if (params.value) {
                    let d = new Date(0);
                    d.setUTCSeconds(params.value);
                    return d.toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hours: "2-digit",
                      minutes: "2-digit",
                    });
                  }
                  return "-";
                }}
              ></AgGridColumn>

              <AgGridColumn
                headerName="Activate/Deactivate"
                field="activePlan"
                cellRenderer="activateSwitchClinic"
              ></AgGridColumn>
            </AgGridReact>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionPlan;
